/**
 * AIAssistant - Assistente AI Agentico per PIGNA Studio
 * 
 * Aiuta gli utenti a costruire flow:
 * - Spiega come usare i nodi
 * - GENERA automaticamente flow completi
 * - Importa nel canvas con un click
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Bot, X, Send, Minimize2, Maximize2, Sparkles, Trash2, Lightbulb, Wand2, Download, Loader2, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { API_URL } from '../config';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  flowData?: any; // Flow generato da importare
}

interface AIAssistantProps {
  isOpen: boolean;
  onClose: () => void;
  onImportFlow?: (flow: any) => void; // Callback per importare flow nel canvas
  userEmail?: string;
}

// Suggerimenti rapidi
const QUICK_SUGGESTIONS = [
  "Come creo un chatbot FAQ?",
  "Spiega il nodo LLM Simple",
  "Quali nodi per inviare email?",
];

// Suggerimenti per generazione flow
const GENERATE_SUGGESTIONS = [
  "Alert news AI ogni 15 min via email",
  "Chatbot che risponde da documenti",
  "Webhook che salva dati su Google Sheets",
];

export default function AIAssistant({ isOpen, onClose, onImportFlow, userEmail }: AIAssistantProps) {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content: `🎯 **Ciao! Sono l'assistente AI di Oystar Studio.**

Posso aiutarti in due modi:

• **💬 Spiegare** come funzionano i nodi
• **🪄 Generare** flow completi automaticamente!

Prova a scrivere cosa vuoi fare, oppure clicca su **"🪄 Genera Flow"** per creare un workflow con un click! 🚀`,
      timestamp: new Date(),
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [mode, setMode] = useState<'chat' | 'generate' | 'agentic'>('chat');
  
  // Agentic loop state
  const [agenticIterations, setAgenticIterations] = useState<any[]>([]);
  const [agenticStatus, setAgenticStatus] = useState<'idle' | 'running' | 'success' | 'failed'>('idle');
  const [, setAgenticFinalFlow] = useState<any>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  // Chat normale
  const sendMessage = useCallback(async (messageText?: string) => {
    const text = messageText || input.trim();
    if (!text || isLoading) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/assistant/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          history: messages.filter(m => m.id !== 'welcome').map(m => ({
            role: m.role,
            content: m.content,
          })),
        }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      
      setMessages(prev => [...prev, {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: data.response || "Mi dispiace, non sono riuscito a elaborare la risposta.",
        timestamp: new Date(),
      }]);

    } catch (error) {
      console.error('❌ Errore AI Assistant:', error);
      setMessages(prev => [...prev, {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: `⚠️ **Errore di connessione**\n\nNon riesco a contattare il server. Riprova tra qualche secondo!`,
        timestamp: new Date(),
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [input, messages, isLoading]);

  // 🪄 GENERA FLOW AUTOMATICAMENTE - TODO: da riattivare quando l'endpoint sarà pronto
  // La funzione generateFlow è temporaneamente commentata perché l'endpoint 
  // /api/assistant/generate-flow non è ancora implementato nel backend

  // Importa flow nel canvas
  const handleImportFlow = useCallback((flowData: any) => {
    if (onImportFlow && flowData) {
      onImportFlow(flowData);
      setMessages(prev => [...prev, {
        id: `imported-${Date.now()}`,
        role: 'assistant',
        content: `🎉 **Flow importato nel canvas!**\n\nOra puoi modificare i nodi e personalizzare le configurazioni. Buon lavoro! 🚀`,
        timestamp: new Date(),
      }]);
    }
  }, [onImportFlow]);

  const clearChat = useCallback(() => {
    setMessages([{
      id: 'welcome',
      role: 'assistant',
      content: `🎯 **Chat pulita!**\n\nSono pronto ad aiutarti. Cosa vuoi creare?`,
      timestamp: new Date(),
    }]);
    // Reset agentic state
    setAgenticIterations([]);
    setAgenticStatus('idle');
    setAgenticFinalFlow(null);
  }, []);

  // 🤖 LOOP AGENTICO con STREAMING: Genera, Testa, Correggi (mostra progressi real-time)
  const generateAndTest = useCallback(async (description?: string) => {
    const text = description || input.trim();
    if (!text || isGenerating) return;

    setInput('');
    setIsGenerating(true);
    setAgenticStatus('running');
    setAgenticIterations([]);
    setAgenticFinalFlow(null);

    // Aggiungi messaggio utente
    setMessages(prev => [...prev, {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date(),
    }]);

    // ID per messaggi dinamici
    const progressMsgId = `agentic-progress-${Date.now()}`;
    let currentProgress = '🚀 **Loop Agentico Avviato**\n\n';
    const progressLines: string[] = [];
    
    // Funzione per aggiornare il messaggio di progresso
    const updateProgressMessage = (newLine: string) => {
      progressLines.push(newLine);
      const content = currentProgress + progressLines.join('\n');
      setMessages(prev => {
        const existing = prev.find(m => m.id === progressMsgId);
        if (existing) {
          return prev.map(m => m.id === progressMsgId ? { ...m, content } : m);
        } else {
          return [...prev, {
            id: progressMsgId,
            role: 'assistant' as const,
            content,
            timestamp: new Date(),
          }];
        }
      });
    };

    try {
      // Usa fetch con streaming (più compatibile di EventSource con CORS)
      const params = new URLSearchParams({
        description: text,
        max_iterations: '5',
      });
      if (userEmail) params.append('user_email', userEmail);
      
      const response = await fetch(`${API_URL}/api/assistant/generate-and-test-stream?${params}`, {
        method: 'GET',
        headers: { 'Accept': 'text/event-stream' },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const reader = response.body?.getReader();
      if (!reader) throw new Error('No reader available');
      
      const decoder = new TextDecoder();
      let buffer = '';
      let finalFlow: any = null;
      let success = false;
      let totalIterations = 0;
      let done = false;

      // Handler per eventi SSE
      const handleEvent = (eventType: string, data: any) => {
        console.log(`📡 SSE [${eventType}]:`, data);
        
        switch (eventType) {
          case 'started':
            updateProgressMessage(`⏳ ${data.message}`);
            break;
            
          case 'iteration':
            updateProgressMessage(`\n📍 **Iterazione ${data.iteration}/${data.total}**`);
            break;
            
          case 'generating':
            updateProgressMessage(`  🧠 Generazione in corso...`);
            break;
            
          case 'generated':
            updateProgressMessage(`  ✅ Flow generato: ${data.nodes} nodi`);
            setAgenticIterations(prev => [...prev, {
              action: 'generate',
              iteration: data.iteration,
              success: true,
              details: `${data.nodes} nodi generati`
            }]);
            break;
            
          case 'testing':
            updateProgressMessage(`  🧪 Test in corso...`);
            break;
            
          case 'tested':
            if (data.success) {
              updateProgressMessage(`  ✅ Test OK: ${data.nodes_executed} nodi in ${data.execution_time_ms}ms`);
            } else {
              updateProgressMessage(`  ❌ Test fallito: ${data.error_preview || 'errore'}`);
            }
            setAgenticIterations(prev => [...prev, {
              action: 'test',
              iteration: data.iteration,
              success: data.success,
              details: data.message
            }]);
            break;
            
          case 'fixing':
            updateProgressMessage(`  🔧 Correzione: ${data.error?.slice(0, 40)}...`);
            break;
            
          case 'fixed':
            updateProgressMessage(`  ✅ Flow corretto: ${data.nodes} nodi`);
            setAgenticIterations(prev => [...prev, {
              action: 'fix',
              iteration: data.iteration,
              success: true,
              details: `${data.nodes} nodi`
            }]);
            break;
            
          case 'completed':
            success = true;
            finalFlow = data.flow;
            totalIterations = data.total_iterations;
            updateProgressMessage(`\n🎉 **${data.message}**`);
            done = true;
            break;
            
          case 'failed':
            success = false;
            finalFlow = data.flow;
            totalIterations = data.total_iterations;
            updateProgressMessage(`\n⚠️ ${data.message}`);
            done = true;
            break;
            
          case 'error':
            updateProgressMessage(`  ❌ ${data.message}`);
            break;
        }
      };

      // Leggi stream
      while (true) {
        const { value, done: readerDone } = await reader.read();
        if (readerDone) break;
        
        buffer += decoder.decode(value, { stream: true });
        
        // Parse SSE events dal buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Mantieni l'ultima linea incompleta
        
        let currentEvent = '';
        let currentData = '';
        
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            currentData = line.slice(6);
          } else if (line === '' && currentEvent && currentData) {
            // Fine evento, processalo
            try {
              const data = JSON.parse(currentData);
              handleEvent(currentEvent, data);
            } catch (e) {
              console.error('Errore parsing SSE data:', e);
            }
            currentEvent = '';
            currentData = '';
          }
        }
        
        if (done) break;
      }
      
      // Finalizza
      setMessages(prev => prev.filter(m => m.id !== progressMsgId));
      
      if (success && finalFlow) {
        setAgenticStatus('success');
        setAgenticFinalFlow(finalFlow);
        setMessages(prev => [...prev, {
          id: `agentic-success-${Date.now()}`,
          role: 'assistant',
          content: `🎉 **Flow Creato e Testato con Successo!**\n\n**Tentativi:** ${totalIterations}\n**Nodi:** ${finalFlow.nodes?.length || 0}\n**Connessioni:** ${finalFlow.edges?.length || 0}\n\n✅ Il flow è pronto per essere importato!`,
          timestamp: new Date(),
          flowData: finalFlow,
        }]);
      } else {
        setAgenticStatus('failed');
        if (finalFlow) setAgenticFinalFlow(finalFlow);
        setMessages(prev => [...prev, {
          id: `agentic-fail-${Date.now()}`,
          role: 'assistant',
          content: `⚠️ **Flow generato ma non completamente testato**\n\n**Tentativi:** ${totalIterations}\n\n${finalFlow ? 'Puoi comunque importarlo e correggerlo manualmente.' : 'Riprova con una descrizione più dettagliata.'}`,
          timestamp: new Date(),
          flowData: finalFlow,
        }]);
      }
      
      setIsGenerating(false);

    } catch (error) {
      console.error('❌ Errore loop agentico:', error);
      setAgenticStatus('failed');
      setMessages(prev => [...prev, {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: `❌ **Errore nel loop agentico**\n\nNon riesco a completare il processo. Verifica la connessione.`,
        timestamp: new Date(),
      }]);
      setIsGenerating(false);
    }
  }, [input, isGenerating, userEmail]);

  if (!isOpen) return null;

  return (
    <div 
      className={`fixed ${isMinimized ? 'bottom-20 right-6 w-80' : 'bottom-20 right-6 w-[450px] h-[650px]'} 
        bg-gradient-to-b from-slate-900 to-slate-800 rounded-2xl shadow-2xl 
        border border-cyan-500/30 z-50 flex flex-col overflow-hidden transition-all duration-300`}
      style={{
        boxShadow: '0 0 40px rgba(6, 182, 212, 0.15), 0 25px 50px -12px rgba(0, 0, 0, 0.5)',
      }}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-600 via-blue-600 to-purple-600 p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Bot size={28} className="text-white" />
            <Sparkles size={12} className="absolute -top-1 -right-1 text-yellow-300 animate-pulse" />
          </div>
          <div>
            <h3 className="font-bold text-white text-lg">Oystar Assistant</h3>
            <span className="text-xs text-cyan-100 flex items-center gap-1">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              {isGenerating ? 'Generando...' : 'AI Agentica'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={clearChat} className="p-2 hover:bg-white/20 rounded-lg transition text-white/70 hover:text-white" title="Pulisci">
            <Trash2 size={18} />
          </button>
          <button onClick={() => setIsMinimized(!isMinimized)} className="p-2 hover:bg-white/20 rounded-lg transition text-white" title={isMinimized ? "Espandi" : "Minimizza"}>
            {isMinimized ? <Maximize2 size={18} /> : <Minimize2 size={18} />}
          </button>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition text-white" title="Chiudi">
            <X size={18} />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Mode Toggle */}
          <div className="flex border-b border-slate-700/50">
            <button
              onClick={() => setMode('chat')}
              className={`flex-1 py-2 text-sm font-medium transition ${mode === 'chat' ? 'bg-slate-700/50 text-cyan-400' : 'text-slate-400 hover:text-white'}`}
            >
              💬 Chat
            </button>
            <button
              onClick={() => setMode('agentic')}
              className={`flex-1 py-2 text-sm font-medium transition ${mode === 'agentic' ? 'bg-slate-700/50 text-amber-400' : 'text-slate-400 hover:text-white'}`}
            >
              🪄 Genera Flow
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map(msg => (
              <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[90%] p-4 rounded-2xl ${
                  msg.role === 'user'
                    ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-br-md'
                    : 'bg-slate-700/80 text-slate-100 rounded-bl-md border border-slate-600/50'
                }`}>
                  <div 
                    className="text-sm leading-relaxed whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{
                      __html: msg.content
                        .replace(/\*\*(.*?)\*\*/g, '<strong class="text-cyan-300">$1</strong>')
                        .replace(/`([^`]+)`/g, '<code class="bg-slate-800 px-1.5 py-0.5 rounded text-amber-300 text-xs">$1</code>')
                        .replace(/^• /gm, '<span class="text-cyan-400">•</span> ')
                    }}
                  />
                  
                  {/* Bottone importa se c'è flowData */}
                  {msg.flowData && onImportFlow && (
                    <button
                      onClick={() => handleImportFlow(msg.flowData)}
                      className="mt-3 w-full py-2 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-white font-bold rounded-lg flex items-center justify-center gap-2 transition-all"
                    >
                      <Download size={18} />
                      📥 Importa nel Canvas
                    </button>
                  )}
                  
                  <span className="text-xs mt-2 block opacity-50">
                    {msg.timestamp.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            ))}
            
            {(isLoading || isGenerating) && (
              <div className="flex justify-start">
                <div className="bg-slate-700/80 text-slate-300 px-4 py-3 rounded-2xl rounded-bl-md border border-slate-600/50">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />
                    <span className="text-sm">{isGenerating ? 'Generando flow...' : 'Pensando...'}</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Quick suggestions based on mode */}
          {messages.length <= 1 && (
            <div className="px-4 pb-2">
              <div className="flex items-center gap-2 mb-2 text-slate-400 text-xs">
                <Lightbulb size={14} />
                <span>
                  {mode === 'agentic' ? 'Esempi di flow da generare:' : 'Domande frequenti:'}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {(mode === 'agentic' ? GENERATE_SUGGESTIONS : QUICK_SUGGESTIONS).map((suggestion, i) => (
                  <button
                    key={i}
                    onClick={() => 
                      mode === 'agentic' ? generateAndTest(suggestion) : sendMessage(suggestion)
                    }
                    className={`px-3 py-1.5 text-xs rounded-full border transition-all ${
                      mode === 'agentic'
                        ? 'bg-amber-600/30 hover:bg-amber-500/50 text-amber-200 border-amber-500/50'
                        : 'bg-slate-700/50 hover:bg-cyan-600/30 text-slate-300 border-slate-600/50'
                    }`}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Agentic Mode Status */}
          {mode === 'agentic' && agenticIterations.length > 0 && (
            <div className="px-4 pb-2">
              <div className="bg-slate-800/80 rounded-xl p-3 border border-slate-700/50">
                <div className="flex items-center gap-2 mb-2">
                  {agenticStatus === 'running' && <RefreshCw size={16} className="text-amber-400 animate-spin" />}
                  {agenticStatus === 'success' && <CheckCircle size={16} className="text-green-400" />}
                  {agenticStatus === 'failed' && <XCircle size={16} className="text-red-400" />}
                  <span className="text-sm font-medium text-slate-300">
                    Loop Agentico: {agenticIterations.length} iterazioni
                  </span>
                </div>
                <div className="space-y-1">
                  {agenticIterations.slice(-5).map((it: any, i: number) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span>{it.success ? '✅' : '❌'}</span>
                      <span className={`font-mono ${
                        it.action === 'generate' ? 'text-purple-400' :
                        it.action === 'test' ? 'text-cyan-400' :
                        'text-amber-400'
                      }`}>
                        {it.action}
                      </span>
                      <span className="text-slate-400 truncate">{it.details}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-slate-700/50 bg-slate-800/50">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    mode === 'agentic' ? generateAndTest() : sendMessage();
                  }
                }}
                placeholder={
                  mode === 'agentic' ? "Descrivi il flow da creare..." : "Chiedi qualcosa..."
                }
                className="flex-1 px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl 
                  text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-all"
                disabled={isLoading || isGenerating}
              />
              {mode === 'agentic' ? (
                <button
                  onClick={() => generateAndTest()}
                  disabled={!input.trim() || isGenerating}
                  className="p-3 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 
                    disabled:from-slate-600 disabled:to-slate-600 disabled:cursor-not-allowed
                    text-white rounded-xl transition-all shadow-lg"
                  title="Genera Flow"
                >
                  {isGenerating ? <Loader2 className="w-5 h-5 animate-spin" /> : <Wand2 size={20} />}
                </button>
              ) : (
                <button
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || isLoading}
                  className="p-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 
                    disabled:from-slate-600 disabled:to-slate-600 disabled:cursor-not-allowed
                    text-white rounded-xl transition-all shadow-lg"
                  title="Invia"
                >
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send size={20} />}
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
